/* =============================================================================
   UNSEEN HAIR — Global JavaScript
   ============================================================================= */

'use strict';

// ── Utility: Debounce ──
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// ── Utility: trapFocus ──
function trapFocus(container, elementToFocus = container) {
  const focusable = container.querySelectorAll(
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  container.removeEventListener('keydown', trapFocusHandler);
  container.addEventListener('keydown', trapFocusHandler);
  (elementToFocus || first).focus();
  function trapFocusHandler(e) {
    if (e.code === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    if (e.code === 'Escape') container.dispatchEvent(new Event('keydown-escape'));
  }
}

function removeTrapFocus(element) {
  if (element) {
    element.removeAttribute('tabindex');
    element.removeEventListener('keydown', trapFocusHandler);
  }
}

// ── Sticky Header ──
const header = document.querySelector('.site-header');
if (header) {
  const onScroll = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 20);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ── Scroll Animations ──
if ('IntersectionObserver' in window) {
  const animObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        animObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });

  document.querySelectorAll('[data-animate]').forEach(el => animObserver.observe(el));
}

// ── Mobile Menu ──
class MobileMenu {
  constructor() {
    this.menu    = document.querySelector('.mobile-menu');
    this.overlay = document.querySelector('.mobile-menu__overlay');
    this.openBtn = document.querySelector('[data-mobile-menu-open]');
    this.closeBtn = this.menu?.querySelector('[data-mobile-menu-close]');
    if (!this.menu) return;
    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.code === 'Escape') this.close(); });

    // Parent submenu accordions — the chevron toggles, it does not navigate
    this.menu.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-submenu]');
        const panel = item && item.querySelector('[data-submenu-panel]');
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.classList.toggle('is-open', !expanded);
      });
    });
  }

  open() {
    this.menu.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    trapFocus(this.menu);
  }

  close() {
    this.menu.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

// ── Cart Drawer ──
class CartDrawer {
  constructor() {
    this.drawer   = document.querySelector('.cart-drawer');
    this.overlay  = document.querySelector('.cart-drawer__overlay');
    this.closeBtn = this.drawer?.querySelector('.cart-drawer__close');
    this.body     = document.getElementById('cart-drawer-body');
    if (!this.drawer) return;

    document.querySelectorAll('[data-open-cart]').forEach(btn =>
      btn.addEventListener('click', () => this.open())
    );
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.code === 'Escape') this.close(); });
    document.addEventListener('cart:open', () => this.open());
  }

  async open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    await this.refresh();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async refresh() {
    if (!this.body) return;
    this.body.innerHTML = '<div class="loading-overlay"><div class="loading-spinner"></div></div>';
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.updateCount(cart.item_count);
      this.updateSubtotal(cart.total_price);
      this.renderItems(cart);
    } catch (e) {
      this.body.innerHTML = '<p style="padding:24px;text-align:center;font-size:1.3rem;color:var(--color-mid);">Could not load cart.</p>';
    }
  }

  renderItems(cart) {
    if (!cart.items || cart.items.length === 0) {
      this.body.innerHTML =
        '<div style="padding:48px 24px;text-align:center;">' +
          '<p style="font-family:var(--font-display);font-weight:300;font-size:1.3rem;letter-spacing:0.12em;text-transform:uppercase;color:var(--color-mid);margin-bottom:12px;">Your cart is empty</p>' +
          '<a href="/collections/all" class="btn btn--primary btn--sm">Browse Extensions</a>' +
        '</div>';
      return;
    }

    var html = '<div class="cart-drawer__items">';
    cart.items.forEach(function(item) {
      var unitPrice = (item.final_price / 100).toFixed(2);
      var linePrice = (item.final_line_price / 100).toFixed(2);
      html +=
        '<div class="cart-drawer__item" data-key="' + item.key + '">' +
          '<div class="cart-drawer__item-img">' +
            (item.image
              ? '<img src="' + item.image + '" alt="' + item.product_title + '" width="72" height="80" style="width:72px;height:80px;object-fit:cover;display:block;background:var(--color-pale);">'
              : '<div style="width:72px;height:80px;background:var(--color-pale);"></div>') +
          '</div>' +
          '<div class="cart-drawer__item-meta">' +
            '<p class="cart-drawer__item-title">' + item.product_title + '</p>' +
            (item.variant_title && item.variant_title !== 'Default Title'
              ? '<p class="cart-drawer__item-variant">' + item.variant_title + '</p>' : '') +
            (item.sku ? '<p class="cart-drawer__item-sku">' + item.sku + '</p>' : '') +
            '<div class="cart-drawer__item-bottom">' +
              '<div class="cart-drawer__qty-wrap">' +
                '<button class="cart-drawer__qty-btn" data-cart-qty-btn data-key="' + item.key + '" data-direction="down" aria-label="Decrease quantity">−</button>' +
                '<input class="cart-drawer__qty-input" type="text" inputmode="numeric" pattern="[0-9]*" value="' + item.quantity + '" data-cart-qty-input="' + item.key + '" aria-label="Quantity" style="width:36px;text-align:center;border:none;border-left:1px solid var(--color-border);border-right:1px solid var(--color-border);font-size:1.3rem;font-weight:200;background:none;padding:4px 0;outline:none;">' +
                '<button class="cart-drawer__qty-btn" data-cart-qty-btn data-key="' + item.key + '" data-direction="up" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<span class="cart-drawer__item-price">£' + linePrice + '</span>' +
              '<button class="cart-drawer__item-remove" data-cart-remove data-key="' + item.key + '" aria-label="Remove item">' +
                '<svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true"><line x1="1" y1="1" x2="10" y2="10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><line x1="10" y1="1" x2="1" y2="10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    });
    html += '</div>';
    this.body.innerHTML = html;

    // Re-bind qty and remove buttons to also refresh drawer
    var self = this;
    this.body.querySelectorAll('[data-cart-qty-btn]').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        var key   = btn.dataset.key;
        var dir   = btn.dataset.direction === 'up' ? 1 : -1;
        var input = self.body.querySelector('[data-cart-qty-input="' + key + '"]');
        if (!input) return;
        var newQty = Math.max(0, parseInt(input.value) + dir);
        input.value = newQty;
        btn.disabled = true;
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: newQty })
          });
          await self.refresh();
        } finally {
          btn.disabled = false;
        }
      });
    });

    this.body.querySelectorAll('[data-cart-remove]').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        btn.disabled = true;
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: btn.dataset.key, quantity: 0 })
        });
        await self.refresh();
      });
    });
  }

  updateCount(count) {
    document.querySelectorAll('.header__cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  updateSubtotal(price) {
    const formatted = (price / 100).toFixed(2);
    document.querySelectorAll('.cart-drawer__subtotal-price').forEach(el => {
      el.textContent = `£${formatted}`;
    });
    /* VAT (UK 20%) and inc-VAT total — display only; Shopify adds VAT at checkout */
    const vat = Math.round(price * 0.2);
    document.querySelectorAll('.cart-drawer__vat-price').forEach(el => {
      el.textContent = `£${(vat / 100).toFixed(2)}`;
    });
    document.querySelectorAll('.cart-drawer__inc-price').forEach(el => {
      el.textContent = `£${((price + vat) / 100).toFixed(2)}`;
    });
  }
}

// ── Accordion ──
document.querySelectorAll('.accordion__button').forEach(btn => {
  btn.addEventListener('click', () => {
    const item = btn.closest('.accordion__item');
    const isOpen = item.classList.contains('is-open');
    // Close all
    document.querySelectorAll('.accordion__item.is-open').forEach(i => i.classList.remove('is-open'));
    if (!isOpen) item.classList.add('is-open');
  });
});

// ── Countdown Timer ──
class CountdownTimer {
  constructor(el) {
    this.el  = el;
    this.end = new Date(el.dataset.end).getTime();
    this.days  = el.querySelector('[data-days]');
    this.hours = el.querySelector('[data-hours]');
    this.mins  = el.querySelector('[data-mins]');
    this.secs  = el.querySelector('[data-secs]');
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  tick() {
    const now  = Date.now();
    const diff = Math.max(0, this.end - now);
    if (diff === 0) { clearInterval(this.interval); this.el.remove(); return; }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000)  / 60000);
    const s = Math.floor((diff % 60000)    / 1000);
    if (this.days)  this.days.textContent  = String(d).padStart(2, '0');
    if (this.hours) this.hours.textContent = String(h).padStart(2, '0');
    if (this.mins)  this.mins.textContent  = String(m).padStart(2, '0');
    if (this.secs)  this.secs.textContent  = String(s).padStart(2, '0');
  }
}

document.querySelectorAll('[data-countdown]').forEach(el => new CountdownTimer(el));

// ── Before/After Slider ──
class BeforeAfterSlider {
  constructor(el) {
    this.el       = el;
    this.before   = el.querySelector('.before-after__before');
    this.divider  = el.querySelector('.before-after__divider');
    this.handle   = el.querySelector('.before-after__handle');
    this.dragging = false;
    this.setPosition(50);
    this.bindEvents();
  }

  bindEvents() {
    const start = (e) => { this.dragging = true; this.move(e); };
    const move  = (e) => { if (this.dragging) this.move(e); };
    const end   = () => { this.dragging = false; };

    this.el.addEventListener('mousedown', start);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);

    this.el.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', end);
  }

  move(e) {
    const rect = this.el.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    this.setPosition(pct);
  }

  setPosition(pct) {
    if (this.before)  this.before.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    if (this.divider) this.divider.style.left = `${pct}%`;
    if (this.handle)  this.handle.style.left  = `${pct}%`;
  }
}

document.querySelectorAll('.before-after').forEach(el => new BeforeAfterSlider(el));

// ── Variant Swatches (inline) ──
document.querySelectorAll('.swatch-list').forEach(list => {
  list.querySelectorAll('.swatch').forEach(swatch => {
    swatch.addEventListener('click', () => {
      list.querySelectorAll('.swatch').forEach(s => s.classList.remove('is-selected'));
      swatch.classList.add('is-selected');
    });
  });
});

// ── Init ──
document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
  new CartDrawer();
});

// ── Product card "Add to Cart" (quick buy) ──
// Delegated so it works for every product pod on collection / search pages,
// including any cards injected later (e.g. recommendations, filtered grids).
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-quick-buy]');
  if (!btn || btn.disabled) return;
  e.preventDefault();

  const variantId = btn.getAttribute('data-quick-buy');
  if (!variantId) return;

  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Adding…';

  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
  fetch(addUrl.replace(/\.js$/, '') + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
    .then((res) => {
      if (!res.ok) return res.json().then((err) => { throw new Error(err.description || 'Add failed'); });
      return res.json();
    })
    .then(() => {
      // A drawer both opens and refreshes (updates the header count) on this event.
      if (document.querySelector('.cart-drawer')) {
        document.dispatchEvent(new Event('cart:open'));
      } else {
        // Page-cart fallback: refresh the header count.
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            document.querySelectorAll('.header__cart-count').forEach((el) => {
              el.textContent = cart.item_count;
              el.style.display = 'flex';
            });
          })
          .catch(() => {});
      }
      btn.textContent = 'Added ✓';
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
    })
    .catch(() => {
      btn.textContent = 'Try again';
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1800);
    });
});
